<template>
  <div class="container mt-4 mb-5" style="max-width: 1080px;">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Administration: Identity Provider</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">Zurück</router-link>
    </div>

    <div v-if="!userStore.isSystemAdmin" class="alert alert-danger">
      Zugriff verweigert: Nur <code>SystemAdmin</code>.
    </div>

    <div v-else>
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="card-title m-0">Provider</h5>
            <div class="d-flex gap-2">
              <button
                class="btn btn-primary btn-sm"
                type="button"
                data-testid="idp-new-button"
                @click="openCreateModal"
              >
                Neuer Provider
              </button>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="reload">Neu laden</button>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typ</th>
                  <th>Identifier</th>
                  <th class="text-end">Aktion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="provider in providers" :key="provider.id">
                  <td>{{ provider.name }}</td>
                  <td>{{ providerTypeLabel(provider.providerType) }}</td>
                  <td class="text-truncate" style="max-width: 520px;">
                    <code>{{ providerIdentifier(provider) }}</code>
                  </td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-primary me-2"
                      type="button"
                      data-testid="idp-edit-button"
                      @click="openEditModal(provider)"
                    >
                      Bearbeiten
                    </button>
                    <button class="btn btn-sm btn-outline-danger" type="button" @click="remove(provider)">Löschen</button>
                  </td>
                </tr>
                <tr v-if="providers.length === 0">
                  <td colspan="4" class="text-center text-muted py-4">Keine Provider gefunden.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" data-testid="idp-modal">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ form.id ? "Identity Provider bearbeiten" : "Neuen Identity Provider anlegen" }}
              </h5>
              <button type="button" class="btn-close" aria-label="Close" @click="closeModal" />
            </div>
            <div class="modal-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label" for="idpName">Name *</label>
                  <input
                    id="idpName"
                    v-model.trim="form.name"
                    class="form-control"
                    type="text"
                    data-testid="idp-name-input"
                    placeholder="z.B. Keycloak Prod"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label" for="idpType">Typ *</label>
                  <select
                    id="idpType"
                    v-model="form.providerType"
                    class="form-select"
                    data-testid="idp-type-select"
                    @change="onProviderTypeChanged"
                  >
                    <option :value="providerTypeValues.oAuth2Oidc">OAuth2 / OIDC</option>
                    <option :value="providerTypeValues.activeDirectoryLdap">Active Directory / LDAP</option>
                  </select>
                </div>

                <template v-if="isOAuth">
                  <div class="col-md-12">
                    <label class="form-label" for="idpIssuer">Issuer *</label>
                    <input
                      id="idpIssuer"
                      v-model.trim="form.issuer"
                      class="form-control"
                      type="text"
                      data-testid="idp-issuer-input"
                      placeholder="https://keycloak.example/realms/realm"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpAuthenticator">Authentifizierer</label>
                    <input
                      id="idpAuthenticator"
                      v-model.trim="form.authenticator"
                      class="form-control"
                      type="text"
                      data-testid="idp-authenticator-input"
                      placeholder="z.B. oidc oder gitlab"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpScopes">Scopes</label>
                    <input
                      id="idpScopes"
                      v-model.trim="form.scopes"
                      class="form-control"
                      type="text"
                      placeholder="openid profile email"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpAuthorizationEndpoint">Authorization Endpoint</label>
                    <input
                      id="idpAuthorizationEndpoint"
                      v-model.trim="form.authorizationEndpoint"
                      class="form-control"
                      type="text"
                      placeholder="https://.../authorize"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpTokenEndpoint">Token Endpoint</label>
                    <input
                      id="idpTokenEndpoint"
                      v-model.trim="form.tokenEndpoint"
                      class="form-control"
                      type="text"
                      placeholder="https://.../token"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpUserInfoEndpoint">UserInfo Endpoint</label>
                    <input
                      id="idpUserInfoEndpoint"
                      v-model.trim="form.userInfoEndpoint"
                      class="form-control"
                      type="text"
                      placeholder="https://.../userinfo"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpClientId">Client ID</label>
                    <input id="idpClientId" v-model.trim="form.clientId" class="form-control" type="text" placeholder="client-id" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpClientSecret">Client Secret</label>
                    <input
                      id="idpClientSecret"
                      v-model="form.clientSecret"
                      class="form-control"
                      type="password"
                      placeholder="client-secret"
                    />
                  </div>

                  <div class="col-md-12">
                    <div class="alert alert-secondary mb-0" data-testid="idp-callback-urls">
                      <div class="fw-semibold mb-2">Callback URLs für die App-Registrierung</div>
                      <ul class="mb-0 ps-3">
                        <li v-for="callbackUrl in callbackUrls" :key="callbackUrl">
                          <code>{{ callbackUrl }}</code>
                        </li>
                      </ul>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="col-md-6">
                    <label class="form-label" for="idpIssuerLdap">Issuer (optional)</label>
                    <input
                      id="idpIssuerLdap"
                      v-model.trim="form.issuer"
                      class="form-control"
                      type="text"
                      data-testid="idp-issuer-input"
                      placeholder="Wenn leer, wird automatisch aus Host/Port erzeugt."
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpLdapHost">LDAP Host *</label>
                    <input
                      id="idpLdapHost"
                      v-model.trim="form.ldapHost"
                      class="form-control"
                      type="text"
                      data-testid="idp-ldap-host-input"
                      placeholder="ad.example.local"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label" for="idpLdapPort">Port</label>
                    <input id="idpLdapPort" v-model.number="form.ldapPort" class="form-control" type="number" min="1" max="65535" />
                  </div>
                  <div class="col-md-8">
                    <label class="form-label" for="idpLdapBaseDn">Base DN *</label>
                    <input id="idpLdapBaseDn" v-model.trim="form.ldapBaseDn" class="form-control" type="text" placeholder="DC=example,DC=local" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpLdapBindDn">Bind DN</label>
                    <input id="idpLdapBindDn" v-model.trim="form.ldapBindDn" class="form-control" type="text" placeholder="CN=svc,OU=Service,DC=example,DC=local" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label" for="idpLdapBindPassword">Bind Passwort</label>
                    <input id="idpLdapBindPassword" v-model="form.ldapBindPassword" class="form-control" type="password" />
                  </div>
                  <div class="col-md-8">
                    <label class="form-label" for="idpLdapFilter">User Search Filter</label>
                    <input id="idpLdapFilter" v-model.trim="form.ldapUserSearchFilter" class="form-control" type="text" placeholder="(sAMAccountName={0})" />
                  </div>
                  <div class="col-md-4 d-flex align-items-end">
                    <div class="form-check mb-2">
                      <input id="idpLdapSsl" v-model="form.ldapUseSsl" class="form-check-input" type="checkbox" />
                      <label for="idpLdapSsl" class="form-check-label">LDAPS (SSL/TLS)</label>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <label class="form-label" for="idpLdapPem">LDAP/LDAPS CA-Zertifikat (PEM)</label>
                    <textarea
                      id="idpLdapPem"
                      v-model="form.ldapCaCertificatePem"
                      class="form-control"
                      rows="4"
                      data-testid="idp-ldap-cert-input"
                      placeholder="-----BEGIN CERTIFICATE-----"
                    />
                  </div>
                </template>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" @click="closeModal">Abbrechen</button>
              <button
                type="button"
                class="btn btn-primary"
                data-testid="idp-save-button"
                :disabled="!canSave"
                @click="save"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showModal" class="modal-backdrop fade show" />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import AdminIdentityProviderService from "@/services/AdminIdentityProviderService";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();

const providerTypeValues = {
  oAuth2Oidc: "OAuth2Oidc",
  activeDirectoryLdap: "ActiveDirectoryLdap",
};

const providers = ref([]);
const callbackUrls = ref([]);
const showModal = ref(false);

const form = reactive(createEmptyForm());

const isOAuth = computed(() => form.providerType === providerTypeValues.oAuth2Oidc);
const canSave = computed(() => {
  if (!form.name) {
    return false;
  }

  if (isOAuth.value) {
    return !!form.issuer;
  }

  return !!form.ldapHost && !!form.ldapBaseDn;
});

function createEmptyForm() {
  return {
    id: null,
    name: "",
    issuer: "",
    providerType: providerTypeValues.oAuth2Oidc,
    authenticator: "",
    authorizationEndpoint: "",
    tokenEndpoint: "",
    userInfoEndpoint: "",
    clientId: "",
    clientSecret: "",
    scopes: "",
    ldapHost: "",
    ldapPort: null,
    ldapBaseDn: "",
    ldapBindDn: "",
    ldapBindPassword: "",
    ldapUserSearchFilter: "",
    ldapUseSsl: false,
    ldapCaCertificatePem: "",
  };
}

function resetForm() {
  Object.assign(form, createEmptyForm());
}

function providerTypeLabel(providerType) {
  return providerType === providerTypeValues.activeDirectoryLdap
    ? "Active Directory / LDAP"
    : "OAuth2 / OIDC";
}

function providerIdentifier(provider) {
  if (provider.providerType === providerTypeValues.activeDirectoryLdap) {
    return provider.ldapHost || provider.issuer || "-";
  }

  return provider.issuer || "-";
}

function toNullableString(value) {
  if (typeof value !== "string") {
    return value ?? null;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function openCreateModal() {
  resetForm();
  showModal.value = true;
}

function openEditModal(provider) {
  Object.assign(form, createEmptyForm(), {
    id: provider.id ?? null,
    name: provider.name ?? "",
    issuer: provider.issuer ?? "",
    providerType: provider.providerType || providerTypeValues.oAuth2Oidc,
    authenticator: provider.authenticator ?? "",
    authorizationEndpoint: provider.authorizationEndpoint ?? "",
    tokenEndpoint: provider.tokenEndpoint ?? "",
    userInfoEndpoint: provider.userInfoEndpoint ?? "",
    clientId: provider.clientId ?? "",
    clientSecret: provider.clientSecret ?? "",
    scopes: provider.scopes ?? "",
    ldapHost: provider.ldapHost ?? "",
    ldapPort: provider.ldapPort ?? null,
    ldapBaseDn: provider.ldapBaseDn ?? "",
    ldapBindDn: provider.ldapBindDn ?? "",
    ldapBindPassword: provider.ldapBindPassword ?? "",
    ldapUserSearchFilter: provider.ldapUserSearchFilter ?? "",
    ldapUseSsl: !!provider.ldapUseSsl,
    ldapCaCertificatePem: provider.ldapCaCertificatePem ?? "",
  });

  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  resetForm();
}

function onProviderTypeChanged() {
  if (isOAuth.value) {
    form.ldapHost = "";
    form.ldapPort = null;
    form.ldapBaseDn = "";
    form.ldapBindDn = "";
    form.ldapBindPassword = "";
    form.ldapUserSearchFilter = "";
    form.ldapUseSsl = false;
    form.ldapCaCertificatePem = "";
    return;
  }

  form.authenticator = "";
  form.authorizationEndpoint = "";
  form.tokenEndpoint = "";
  form.userInfoEndpoint = "";
  form.clientId = "";
  form.clientSecret = "";
  form.scopes = "";
}

async function loadProviders() {
  const response = await AdminIdentityProviderService.getProviders();
  providers.value = Array.isArray(response?.data) ? response.data : [];
}

async function loadCallbackUrls() {
  const response = await AdminIdentityProviderService.getCallbackUrls();
  callbackUrls.value = Array.isArray(response?.data?.callbackUrls)
    ? response.data.callbackUrls
    : [];
}

async function save() {
  const payload = {
    id: form.id,
    name: form.name,
    issuer: form.issuer,
    providerType: form.providerType,
    authenticator: isOAuth.value ? toNullableString(form.authenticator) : null,
    authorizationEndpoint: isOAuth.value ? toNullableString(form.authorizationEndpoint) : null,
    tokenEndpoint: isOAuth.value ? toNullableString(form.tokenEndpoint) : null,
    userInfoEndpoint: isOAuth.value ? toNullableString(form.userInfoEndpoint) : null,
    clientId: isOAuth.value ? toNullableString(form.clientId) : null,
    clientSecret: isOAuth.value ? toNullableString(form.clientSecret) : null,
    scopes: isOAuth.value ? toNullableString(form.scopes) : null,
    ldapHost: !isOAuth.value ? toNullableString(form.ldapHost) : null,
    ldapPort: !isOAuth.value ? form.ldapPort : null,
    ldapBaseDn: !isOAuth.value ? toNullableString(form.ldapBaseDn) : null,
    ldapBindDn: !isOAuth.value ? toNullableString(form.ldapBindDn) : null,
    ldapBindPassword: !isOAuth.value ? toNullableString(form.ldapBindPassword) : null,
    ldapUserSearchFilter: !isOAuth.value ? toNullableString(form.ldapUserSearchFilter) : null,
    ldapUseSsl: !isOAuth.value ? !!form.ldapUseSsl : false,
    ldapCaCertificatePem: !isOAuth.value ? toNullableString(form.ldapCaCertificatePem) : null,
  };

  await AdminIdentityProviderService.upsertProvider(payload);
  closeModal();
  await loadProviders();
}

async function remove(provider) {
  const confirmed = window.confirm(`Provider "${provider.name}" wirklich löschen?`);
  if (!confirmed) {
    return;
  }

  await AdminIdentityProviderService.deleteProvider(provider.id);
  await loadProviders();
}

async function reload() {
  await Promise.all([loadProviders(), loadCallbackUrls()]);
}

onMounted(async () => {
  if (!userStore.me) {
    try {
      await userStore.loadMe();
    } catch (error) {
      console.error("Failed to load /user/me:", error);
    }
  }

  if (userStore.isSystemAdmin) {
    await reload();
  }
});
</script>
